/**
 * Job actions as menu items (job page v2). Row ⋯ menus and right-click menus
 * render the registry's actions through this, so a menu can never word or
 * run an action differently from ⌘K or a pin.
 */
import { Fragment } from "react";
import { ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from "@/components/ui/context-menu";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { JobAction, JobActionId } from "./jobActions";

export function JobActionMenuItems({
    actions,
    ids,
    showUnavailable = false,
    groupLabels = false,
    kind = "dropdown",
    testIdPrefix = "job-action",
}: {
    actions: JobAction[];
    /** Which actions, in this order. Omit for all of them (list order). */
    ids?: JobActionId[];
    /** Show greyed actions with their reason (default: hide them). */
    showUnavailable?: boolean;
    /** Label each group. */
    groupLabels?: boolean;
    /** A ⋯ dropdown or a right-click menu. */
    kind?: "dropdown" | "context";
    testIdPrefix?: string;
}) {
    const Item = kind === "context" ? ContextMenuItem : DropdownMenuItem;
    const Label = kind === "context" ? ContextMenuLabel : DropdownMenuLabel;
    const Sep = kind === "context" ? ContextMenuSeparator : DropdownMenuSeparator;
    const byId = new Map(actions.map((a) => [a.id, a]));
    const list = (ids ? ids.map((id) => byId.get(id)).filter((a): a is JobAction => !!a) : actions).filter(
        (a) => showUnavailable || a.applies,
    );
    let lastGroup: string | null = null;
    return (
        <>
            {list.map((a) => {
                const newGroup = a.group !== lastGroup;
                const first = lastGroup === null;
                lastGroup = a.group;
                const Icon = a.icon;
                return (
                    <Fragment key={a.id}>
                        {newGroup && !first && groupLabels && <Sep />}
                        {newGroup && groupLabels && (
                            <Label className="text-[12px] text-muted-foreground">{a.group}</Label>
                        )}
                        {a.destructive && !first && !groupLabels && <Sep />}
                        <Item
                            disabled={!a.applies}
                            onSelect={() => a.run()}
                            data-testid={`${testIdPrefix}-${a.id}`}
                            className={cn(a.destructive && "text-red-700 dark:text-red-400")}
                        >
                            <Icon className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span className="min-w-0 flex-1">{a.label}</span>
                            {!a.applies && a.reason && (
                                <span className="ml-3 text-[12px] text-muted-foreground">{a.reason}</span>
                            )}
                        </Item>
                    </Fragment>
                );
            })}
        </>
    );
}
